import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Alert,
} from '@mui/material';
import { useFormik } from 'formik';
import { Client, UpdateClientDTO, ClientFormValues } from '../../types/client';
import { getRepresentatives } from '../../api/users';
import { getActiveLookupsByType } from '../../api/lookups';
import { User } from '../../types/user.types';
import { useAuth } from '../../context/AuthContext';

// Import section components
import BusinessInformationSection from './sections/BusinessInformationSection';
import ContactInformationSection from './sections/ContactInformationSection';
import RegisteredAddressSection from './sections/RegisteredAddressSection';
import TradingAddressSection from './sections/TradingAddressSection';
import SoleTraderDetailsSection from './sections/SoleTraderDetailsSection';
import TradingDurationSection from './sections/TradingDurationSection';
import HomeAddressSection from './sections/HomeAddressSection';
import AddressHistorySection from './sections/AddressHistorySection';
import InternalInformationSection from './sections/InternalInformationSection';
import AdditionalNotesSection from './sections/AdditionalNotesSection';

// Import validation schema
import { clientValidationSchema } from './validation/clientValidationSchema';

// Business types constant
const BUSINESS_TYPES = ['Limited Company', 'Sole Trader', 'Charity / Trust / Community Org'] as const;

// Helper functions for date parsing and age calculation
export const parseDateOfBirth = (dateOfBirth: any): { day: string; month: string; year: string } => {
  if (!dateOfBirth) {
    return { day: '', month: '', year: '' };
  }

  // Handle MongoDB date format: { "$date": { "$numberLong": "timestamp" } }
  if (typeof dateOfBirth === 'object' && dateOfBirth.$date) {
    const timestamp = dateOfBirth.$date.$numberLong 
      ? parseInt(dateOfBirth.$date.$numberLong) 
      : dateOfBirth.$date;
    
    const date = new Date(timestamp);
    return {
      day: date.getDate().toString().padStart(2, '0'),
      month: (date.getMonth() + 1).toString().padStart(2, '0'),
      year: date.getFullYear().toString()
    };
  }

  // Handle Date object
  if (dateOfBirth instanceof Date) {
    return {
      day: dateOfBirth.getDate().toString().padStart(2, '0'),
      month: (dateOfBirth.getMonth() + 1).toString().padStart(2, '0'),
      year: dateOfBirth.getFullYear().toString()
    };
  }

  // Handle string date
  if (typeof dateOfBirth === 'string') {
    const date = new Date(dateOfBirth);
    if (!isNaN(date.getTime())) {
      return {
        day: date.getDate().toString().padStart(2, '0'),
        month: (date.getMonth() + 1).toString().padStart(2, '0'),
        year: date.getFullYear().toString()
      };
    }
  }

  return { day: '', month: '', year: '' };
};

export const calculateAge = (year: string, month: string, day: string): number => {
  if (!year || !month || !day) return 0;
  
  const birthDate = new Date(parseInt(year), parseInt(month) - 1, parseInt(day));
  const today = new Date();
  
  let age = today.getFullYear() - birthDate.getFullYear();
  const monthDiff = today.getMonth() - birthDate.getMonth();
  
  if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getDate())) {
    age--;
  }
  
  return age;
};
// Local interface extending the shared interface if needed
export interface FormValues extends ClientFormValues {
  // Any additional fields specific to EditClientDialog can be added here
  // All common fields are already defined in ClientFormValues
}

interface EditClientDialogProps {
  open: boolean;
  onClose: () => void;
  client: Client;
  onSave: (values: UpdateClientDTO) => void;
}

const EditClientDialog: React.FC<EditClientDialogProps> = ({ open, onClose, client, onSave }) => {
  // State management for data fetching
  const [error, setError] = useState<string | null>(null);
  const [representatives, setRepresentatives] = useState<User[]>([]);
  const [industries, setIndustries] = useState<string[]>([]);
  const [partners, setPartners] = useState<string[]>([]);
  const [position, setPosition] = useState<string[]>([]);
  const { user } = useAuth();
  const isAdmin = user?.role === 'administrator';

  
  // Data fetching effect
  useEffect(() => {
    const fetchData = async () => {
      try {
        // Fetch representatives
        const reps = await getRepresentatives();
        setRepresentatives(reps);
        
        // Fetch industries
        const industriesLookup = await getActiveLookupsByType('Industry');
        const industriesValues = industriesLookup.map(lookup => lookup.value);
        setIndustries(industriesValues);
        
        // Fetch partners
        const partnersLookup = await getActiveLookupsByType('Partners');
        const partnersValues = partnersLookup.map(lookup => lookup.value);
        setPartners(partnersValues);
        
        // Fetch positions
        const positionsLookup = await getActiveLookupsByType('Positions');
        const positionsValues = positionsLookup.map(lookup => lookup.value);
        setPosition(positionsValues);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Failed to load form data. Please try again.');
      }
    };
    
    fetchData();
  }, []);

// Parse date of birth from client data
const dobParsed = parseDateOfBirth(client.dateOfBirth);

// Format incorporation date from ISO to DD/MM/YYYY for display
const formatIncorporationDate = (dateString?: string): string => {
  if (!dateString) return '';
  try {
    const date = new Date(dateString);
    if (isNaN(date.getTime())) return '';
    return `${date.getDate().toString().padStart(2, '0')}/${(date.getMonth() + 1).toString().padStart(2, '0')}/${date.getFullYear()}`;
  } catch (error) {
    console.error('Error formatting incorporation date:', error);
    return '';
  }
};

// Parse DD/MM/YYYY back to ISO format for server submission
const parseIncorporationDate = (formattedDate?: string): string | undefined => {
  if (!formattedDate) return undefined;
  try {
    // Split the DD/MM/YYYY format
    const [day, month, year] = formattedDate.split('/');
    if (!day || !month || !year) return undefined;
    
    // Create a new date in ISO format (YYYY-MM-DD)
    const date = new Date(`${year}-${month}-${day}`);
    if (isNaN(date.getTime())) return undefined;
    
    return date.toISOString();
  } catch (error) {
    console.error('Error parsing incorporation date:', error);
    return undefined;
  }
};

// Helper function to normalize business type for legacy data
const normalizeBusinessType = (type: string): typeof BUSINESS_TYPES[number] => {
  // Handle legacy data that may have different formatting
  if (type === 'Charity/Trust/Community Org') {
    return 'Charity / Trust / Community Org';
  }
  // Return as valid business type or default
  return (BUSINESS_TYPES.includes(type as any) ? type : 'Limited Company') as typeof BUSINESS_TYPES[number];
};

// Initialize formik with proper typing
const formik = useFormik<FormValues>({
  initialValues: {
    companyName: client.companyName || '',
    ownerName: client.ownerName || '',
    email: client.email || '',
    phoneNumber: client.phoneNumber || '',
    businessType: normalizeBusinessType(client.businessType) || 'Limited Company',
    representativeId: client.representativeId 
      ? (typeof client.representativeId === 'object' 
          ? ((client.representativeId as any)._id?.toString() || (client.representativeId as any).id?.toString() || '') 
          : client.representativeId.toString()) 
      : '',
    industry: client.industry || '',
    partner: client.partner || '',
    position: client.position || '',
    
    // Trading Address
    tradingAddressLine1: client.tradingAddress?.line1 || '',
    tradingAddressLine2: client.tradingAddress?.line2 || '',
    tradingCity: client.tradingAddress?.city || '',
    tradingCounty: client.tradingAddress?.county || '',
    tradingPostcode: client.tradingAddress?.postcode || '',
    
    // Registered Address
    registeredAddressLine1: client.registeredBusinessAddress?.line1 || '',
    registeredAddressLine2: client.registeredBusinessAddress?.line2 || '',
    registeredCity: client.registeredBusinessAddress?.city || '',
    registeredCounty: client.registeredBusinessAddress?.county || '',
    registeredPostcode: client.registeredBusinessAddress?.postcode || '',
    
    // Home Address
    homeAddressLine1: client.homeAddress?.line1 || '',
    homeAddressLine2: client.homeAddress?.line2 || '',
    homeCity: client.homeAddress?.city || '',
    homeCounty: client.homeAddress?.county || '',
    homePostcode: client.homeAddress?.postcode || '',
    homeMovedInDate: client.homeAddress?.movedInDate ? (() => {
      const dateValue = client.homeAddress.movedInDate;
      // Handle MongoDB extended JSON format
      if (typeof dateValue === 'object' && (dateValue as any).$date) {
        const timestamp = (dateValue as any).$date.$numberLong 
          ? parseInt((dateValue as any).$date.$numberLong) 
          : (dateValue as any).$date;
        return new Date(timestamp);
      }
      // Handle regular Date or ISO string
      return new Date(dateValue);
    })() : null,
    
    // Address History
    addressHistory: client.addressHistory || [],
    
    // Business specific fields
    companyNumber: client.companyNumber || '',
    incorporationDate: formatIncorporationDate(client.incorporationDate),
    
    // Sole Trader fields
    dobDay: dobParsed.day,
    dobMonth: dobParsed.month,
    dobYear: dobParsed.year,
    yearsTrading: client.yearsTrading?.toString() || '',
    monthsTrading: client.monthsTrading?.toString() || '',
    
    notes: client.notes || '',
  },
  validationSchema: clientValidationSchema,
  onSubmit: (values: FormValues) => {
    if (onSave) {
      // Reassemble the split DOB fields into the canonical dateOfBirth —
      // without this the PUT carried dobDay/dobMonth/dobYear, which the
      // server whitelist strips, so DOB edits were silently dropped.
      const { dobDay, dobMonth, dobYear, ...rest } = values;
      // Convert string values to numbers for yearsTrading and monthsTrading
      // and create a properly typed UpdateClientDTO object
      const updateClientDTO: UpdateClientDTO = {
        ...rest,
        yearsTrading: values.yearsTrading ? parseInt(values.yearsTrading) : undefined,
        monthsTrading: values.monthsTrading ? parseInt(values.monthsTrading) : undefined,
        dateOfBirth: dobYear && dobMonth && dobDay
          ? `${dobYear}-${String(dobMonth).padStart(2, '0')}-${String(dobDay).padStart(2, '0')}`
          : undefined,
        // Convert incorporation date from DD/MM/YYYY to ISO format
        incorporationDate: values.incorporationDate ? parseIncorporationDate(values.incorporationDate) : undefined,
        // Ensure all required fields for UpdateClientDTO are properly typed
        tradingAddress: {
          line1: values.tradingAddressLine1 || '',
          line2: values.tradingAddressLine2,
          city: values.tradingCity || '',
          county: values.tradingCounty,
          postcode: values.tradingPostcode || '',
          country: 'United Kingdom' // Add the required country field
        }
      };
      
      // Add registered address only for Limited Companies and Charities
      if (values.businessType !== 'Sole Trader') {
        updateClientDTO.registeredBusinessAddress = {
          line1: values.registeredAddressLine1 || '',
          line2: values.registeredAddressLine2,
          city: values.registeredCity || '',
          county: values.registeredCounty,
          postcode: values.registeredPostcode || ''
        };
      }
      
      // Add home address for Sole Traders and Limited Companies (if provided)
      if (values.businessType === 'Sole Trader' || (values.homeAddressLine1 && values.homeCity && values.homePostcode)) {
        updateClientDTO.homeAddress = {
          line1: values.homeAddressLine1 || '',
          line2: values.homeAddressLine2,
          city: values.homeCity || '',
          county: values.homeCounty,
          postcode: values.homePostcode || '',
          movedInDate: values.homeMovedInDate ? values.homeMovedInDate.toISOString() : undefined
        };
        
        // Add address history if provided
        if (values.addressHistory && values.addressHistory.length > 0) {
          updateClientDTO.addressHistory = values.addressHistory;
        }
      }
      
      onSave(updateClientDTO);
    }
  },
});

  return (
    <Dialog 
      open={open} 
      onClose={onClose} 
      maxWidth="md" 
      fullWidth
      PaperProps={{
        sx: {
          backgroundColor: 'rgba(30, 41, 59, 0.95)',
          backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255, 255, 255, 0.2)',
          borderRadius: 2,
          boxShadow: '0 10px 15px -3px rgba(0, 0, 0, 0.1), 0 4px 6px -2px rgba(0, 0, 0, 0.05)',
          color: 'white',
        }
      }}
    >
      <DialogTitle sx={{ color: 'white' }}>Edit Client</DialogTitle>
      
      <form onSubmit={formik.handleSubmit}>
        <DialogContent sx={{ color: 'white' }}>
          {error && <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#ff8a80' }}>{error}</Alert>}
          
          {formik.submitCount > 0 && Object.keys(formik.errors).length > 0 && (
            <Alert severity="error" sx={{ mb: 2, backgroundColor: 'rgba(211, 47, 47, 0.15)', color: '#ff8a80' }}>
              Please fix the validation errors below before saving:
              <ul style={{ margin: '8px 0 0 0', paddingLeft: '20px' }}>
                {Object.entries(formik.errors).slice(0, 5).map(([field, error]) => (
                  <li key={field}>{String(error)}</li>
                ))}
                {Object.keys(formik.errors).length > 5 && (
                  <li>...and {Object.keys(formik.errors).length - 5} more errors</li>
                )}
              </ul>
            </Alert>
          )}
          
          <Grid container spacing={2}>
            <BusinessInformationSection formik={formik as any} businessTypes={BUSINESS_TYPES} isEditing={true} />
            <ContactInformationSection formik={formik as any} position={position} />

            {(formik.values.businessType === 'Limited Company' || 
              formik.values.businessType === 'Charity / Trust / Community Org') && (
              <RegisteredAddressSection 
                formik={formik as any} 
                clientId={client._id?.toString()} 
                onRefreshSuccess={(updatedClient) => {
                  // Update the form with refreshed data
                  if (updatedClient.incorporationDate) {
                    const formattedDate = formatIncorporationDate(updatedClient.incorporationDate);
                    formik.setFieldValue('incorporationDate', formattedDate);
                  }
                }}
              />
            )}

            <TradingAddressSection formik={formik as any} showCopyButton={true} />

            {client.businessType === 'Sole Trader' && (
              <TradingDurationSection formik={formik} />
            )}
            
            <HomeAddressSection formik={formik} />
            
            {client.businessType === 'Sole Trader' && (
              <>
                <AddressHistorySection formik={formik} />
                
                <SoleTraderDetailsSection 
                  formik={formik} 
                  calculateAge={calculateAge}
                />
              </>
            )}

            <InternalInformationSection
              formik={formik as any}
              isAdmin={isAdmin}
              representatives={representatives}
              industries={industries as any}
              partners={partners as any}
            />

            <AdditionalNotesSection formik={formik as any} />
          </Grid>
        </DialogContent>
        
        <DialogActions sx={{ padding: '16px 24px' }}>
          <Button 
            onClick={onClose}
            sx={{
              color: 'rgba(255, 255, 255, 0.7)',
              '&:hover': {
                color: 'white',
                backgroundColor: 'rgba(255, 255, 255, 0.08)'
              }
            }}
          >
            Cancel
          </Button>
          <Button 
            type="submit" 
            variant="contained" 
            color="primary"
            sx={{
              backgroundColor: 'primary.main',
              '&:hover': {
                backgroundColor: 'primary.dark',
              },
              backdropFilter: 'blur(4px)',
              boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
            }}
          >
            Save Changes
          </Button>
        </DialogActions>
      </form>
    </Dialog>
  );
};

export default EditClientDialog;